import { ENCRYPTION_KEY, IS_ENCRYPTION_ENABLED } from '@/constants/api';
import { clearSession, readSessionToken } from '@/lib/auth';
import { gameExit } from '@/lib/game-exit';
import { delayMs, isConnectionFailure } from '@/services/connection-retry';
import { isGameApiRequest } from '@/services/game-api-scope';
import {
  clearAxiosRequestDedupeEntry,
  registerAxiosRequestDedupeInterceptor,
} from '@/services/request-dedupe';
import { decryptPayload, encryptPayload, isEncryptedPayload } from '@/utils/crypto';
import { appConfig } from '@app/config/appConfig';
import axiosLib, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import i18next from 'i18next';
import { toast } from 'sonner';

const CONNECTION_RETRY_COUNT_KEY = '__mazeConnectionRetryCount__' as const;
const CONNECTION_RETRY_LIMIT = 3;
const CONNECTION_RETRY_INTERVAL_MS = 1000;
const TOKEN_RETRY_KEY = '__mazeTokenRetryDone__' as const;

export const resolveGameApiBase = (): string => appConfig.api.baseUrl;

export const resolveAdminBase = (): string => {
  const admin = appConfig.api.adminBaseUrl;
  return admin.length > 0 ? admin : resolveGameApiBase();
};

export const BASE_URL = resolveGameApiBase();

const axiosClient = axiosLib.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(BASE_URL.includes('ngrok') ? { 'ngrok-skip-browser-warning': '69420' } : {}),
  },
});

registerAxiosRequestDedupeInterceptor(axiosClient);

axiosClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = readSessionToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }

    if (
      IS_ENCRYPTION_ENABLED &&
      isGameApiRequest(config, BASE_URL, resolveAdminBase()) &&
      config.data != null &&
      !isEncryptedPayload(config.data)
    ) {
      if (!ENCRYPTION_KEY) {
        throw new Error('VITE_ENCRYPTION_KEY is required when encryption is enabled');
      }
      config.data = await encryptPayload(config.data, ENCRYPTION_KEY);
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error instanceof Error ? error : new Error(String(error))),
);

axiosClient.interceptors.response.use(
  async (response) => {
    clearAxiosRequestDedupeEntry(response.config);
    if (
      IS_ENCRYPTION_ENABLED &&
      ENCRYPTION_KEY &&
      isGameApiRequest(response.config, BASE_URL, resolveAdminBase()) &&
      isEncryptedPayload(response.data)
    ) {
      response.data = await decryptPayload(response.data, ENCRYPTION_KEY);
    }
    return response;
  },
  async (error: AxiosError) => {
    const cfg = error.config;
    if (cfg) {
      clearAxiosRequestDedupeEntry(cfg);
    }

    if (axiosLib.isCancel(error) || error.code === 'ERR_CANCELED') {
      throw error instanceof Error ? error : new Error(String(error));
    }

    const config = error.config as
      | (InternalAxiosRequestConfig & {
          [CONNECTION_RETRY_COUNT_KEY]?: number;
          [TOKEN_RETRY_KEY]?: boolean;
        })
      | undefined;
    const status = error.response?.status;
    const connectionRetryCount = config?.[CONNECTION_RETRY_COUNT_KEY] ?? 0;

    if (config && isConnectionFailure(error) && connectionRetryCount < CONNECTION_RETRY_LIMIT) {
      config[CONNECTION_RETRY_COUNT_KEY] = connectionRetryCount + 1;
      await delayMs(CONNECTION_RETRY_INTERVAL_MS);
      return axiosClient.request(config);
    }

    if (status === 401) {
      clearSession();
      gameExit(
        {
          closeTab: i18next.t(
            'common.sessionExpiredTab',
            'Session expired. You may close this tab.',
          ),
          appBack: i18next.t('common.sessionExpiredApp', 'Session expired. Returning...'),
        },
        (msg) => {
          toast.error(msg);
        },
      ).catch(() => {});
    }

    throw error;
  },
);

export default axiosClient;
